import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Eraser, Check, FileText } from 'lucide-react';
import { toast } from 'sonner';

export const SignableDocument = ({ formData, onSign, disabled = false, readOnly = false, signatureData = null }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasAuthorization, setHasAuthorization] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  React.useEffect(() => {
    if (readOnly) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Fill canvas with white so exported PNG has white background (not transparent)
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw authorization line
    ctx.beginPath();
    ctx.strokeStyle = '#E4E4E7';
    ctx.moveTo(20, canvas.height - 30);
    ctx.lineTo(canvas.width - 20, canvas.height - 30);
    ctx.stroke();
    ctx.strokeStyle = '#000000';
  }, [readOnly]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    if (disabled || readOnly) return;
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasAuthorization(true);
  };

  const draw = (e) => {
    if (!isDrawing || disabled || readOnly) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearAuthorization = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Fill with white instead of clearRect (avoids transparent background)
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.beginPath();
    ctx.strokeStyle = '#E4E4E7';
    ctx.moveTo(20, canvas.height - 30);
    ctx.lineTo(canvas.width - 20, canvas.height - 30);
    ctx.stroke();
    ctx.strokeStyle = '#000000';
    
    setHasAuthorization(false);
  };

  const handleAuthorize = () => {
    if (!hasAuthorization || !agreedToTerms) {
      toast.error('Please provide authorization and agree to the terms');
      return;
    }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSign(dataUrl);
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="max-w-4xl mx-auto" data-testid="signable-document">
      {/* Document Preview */}
      <div className="bg-white border-2 border-gray-300 shadow-lg mb-6">
        {/* American Flag Header - Exact match to original document */}
        <div className="bg-white p-3 border-b flex items-start gap-3">
          <div className="flex-shrink-0" style={{ width: '45px', height: '24px', position: 'relative', border: '1px solid #ccc', marginTop: '2px' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
              {[...Array(13)].map((_, i) => (
                <div 
                  key={i} 
                  style={{ 
                    height: '7.69%', 
                    backgroundColor: i % 2 === 0 ? '#B22234' : '#FFFFFF' 
                  }} 
                />
              ))}
            </div>
            <div style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              width: '40%', 
              height: '53.85%', 
              backgroundColor: '#3C3B6E',
              display: 'flex',
              flexWrap: 'wrap',
              alignContent: 'center',
              justifyContent: 'center',
              gap: '1px',
              padding: '2px'
            }}>
              {[...Array(9)].map((_, i) => (
                <div key={i} style={{ width: '2px', height: '2px', backgroundColor: 'white', borderRadius: '50%' }} />
              ))}
            </div>
          </div>
          <div className="flex flex-col text-[7px] leading-tight">
            <span className="text-gray-800 font-bold">
              :C.-S.-S.-C.-P.-S.-G.-FLAG OF THE DOCUMENT-CONTRACT-POSTAL-VESSEL-COURT.
            </span>
            <span className="text-gray-600 mt-1">
              We Provide the Correct Claims Assistance for those that are navigating the Judicial System.
            </span>
            <span className="text-gray-600">
              We are not Attorneys or Lawyers, though we may act as an Attorney in Fact or the JUDGE in the court of record as the Constitution dictates. We represent our Claim with you in their Court if you have Joinder and must follow through with an active Case. We Litigate as well as Claim.
            </span>
          </div>
        </div>
        
        {/* Website Links */}
        <div className="px-4 py-2 bg-gray-50 border-b">
          <p className="text-xs text-blue-600">
            <a href="https://www.ToddProffit.com" target="_blank" rel="noopener noreferrer">www.ToddProffit.com</a> • <a href="https://QLCLLC.com" target="_blank" rel="noopener noreferrer">QLCLLC.com</a>
          </p>
        </div>

        {/* Case Qualification Steps */}
        <div className="p-4 border-b">
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            CASE QUALIFICATION
          </h2>
          <p className="text-sm mb-2">In order to qualify your case for our program please perform the following steps:</p>
          <ol className="text-sm space-y-2 list-decimal list-inside">
            <li>Fill out this form: Please be thorough.</li>
            <li>Send your ORIGIN case file, original foreclosure notice, criminal complaint, Arrest Warrant, etc….</li>
            <li>Pay the Deposit Fee $2,500.00 and send that receipt with your Origin Case filing, email to: <strong>AspenLegal@USA.com</strong></li>
            <li>If we can take your case we will notify you within 24 hours of your deposit.</li>
            <li>Upon receiving the $2,500.00 in consideration we will have a proposal within 24 hours. If not, we will refund your monies next day. If we can take your case, we will create your Claim within 7-10 business days.</li>
            <li>Upon completion of the Claim Package we provide the option of Bonding the Claim for 1/12 of 1% and underwriting fee-$3,000.00. (The Bonding is not required, you can claim without it and work on collections independently.) If you would like to partner it's- $1 million in Claim to Bond: Excelsior as the surety, and EPIT for the Bond.</li>
            <li>All cases are bonded for damages. We include a Performance Bond, for our performance of $1 million. We collect from the Vassalee(s) after closure.</li>
            <li>The Bonding is necessary on or before you file your claim. We underwrite it prior to Claim, Filed on the 100th day of your Claim. If you choose to underwrite it, you can add to the Bond up to the cap. Which is your claims damages total.</li>
          </ol>
        </div>

        {/* Bond Exchange Info */}
        <div className="p-4 bg-yellow-50 border-b text-sm">
          <p className="mb-2">
            <strong>Bonds can be marketed in the private exchange.</strong> Go to <a href="https://www.EPITBank.com" target="_blank" rel="noopener noreferrer" className="text-blue-600">www.EPITBank.com</a> for videos on the Bond Exchange used to: sell, assign, trade and pool assets.
          </p>
          <p className="text-xs text-gray-600">
            The credibility of the Vassalees, Insurance, Surety, and Claim determine the value and potential return. The exchange taps into the Private Bond Market: Investors, Banks, Trusts, Developers, Credit facilities, Collections, Family Offices, QFS decentralized exchanges for crypto, tokens, and more.
          </p>
          <p className="mt-2 text-xs">
            You will receive a contract offer upon completion of your Claim. A 48 hour review, with underwriting, will provide at least 2 proposals to choose from.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Client Information Section */}
          <section>
            <h2 className="text-lg font-bold border-b-2 border-black pb-2 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              CLIENT INFORMATION
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Name:</span>
                <p className="font-medium border-b border-gray-300 pb-1">{formData.name || '_______________'}</p>
              </div>
              <div>
                <span className="text-gray-500">Address:</span>
                <p className="font-medium border-b border-gray-300 pb-1">{formData.address || '_______________'}</p>
              </div>
              <div>
                <span className="text-gray-500">Email:</span>
                <p className="font-medium border-b border-gray-300 pb-1">{formData.email || '_______________'}</p>
              </div>
              <div>
                <span className="text-gray-500">Phone:</span>
                <p className="font-medium border-b border-gray-300 pb-1">{formData.phone || '_______________'}</p>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500">How you like to communicate:</span>
                <p className="font-medium border-b border-gray-300 pb-1">{formData.communication_preference || 'email/phone'}</p>
              </div>
            </div>
          </section>

          <Separator />

          {/* Seven Questions */}
          <section>
            <h2 className="text-lg font-bold border-b-2 border-black pb-2 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              SEVEN QUESTIONS
            </h2>
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-gray-500">1. Who are the Claimants?</p>
                <p className="font-medium border-b border-gray-300 pb-1 min-h-[24px]">{formData.q1_claimants || ''}</p>
              </div>
              <div>
                <p className="text-gray-500">2. Who are you making claim against - multiple parties - multiple companies?</p>
                <p className="font-medium border-b border-gray-300 pb-1 min-h-[24px]">{formData.q2_defendants || ''}</p>
              </div>
              <div>
                <p className="text-gray-500">3. Who or what outcome do you want from the case?</p>
                <p className="font-medium border-b border-gray-300 pb-1 min-h-[24px]">{formData.q3_desired_outcome || ''}</p>
              </div>
              <div>
                <p className="text-gray-500">4. What damages do you feel you are owed?</p>
                <p className="font-medium border-b border-gray-300 pb-1 min-h-[24px]">{formData.q4_damages || ''}</p>
              </div>
              <div>
                <p className="text-gray-500">5. What will reconcile the negative consequences?</p>
                <p className="font-medium border-b border-gray-300 pb-1 min-h-[24px]">{formData.q5_other_damages || ''}</p>
              </div>
              <div>
                <p className="text-gray-500">6. How has this affected your family and/or business partners?</p>
                <p className="font-medium border-b border-gray-300 pb-1 min-h-[24px]">{formData.q6_impact || ''}</p>
              </div>
              <div>
                <p className="text-gray-500">7. Tell the story to a 6 year old?</p>
                <p className="font-medium border-b border-gray-300 pb-1 min-h-[24px]">{formData.q7_simple_story || ''}</p>
              </div>
            </div>
          </section>

          <Separator />

          {/* Contract Header */}
          <section className="bg-gray-900 text-white p-4 -mx-6">
            <h1 className="text-xl font-bold text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              EPIT PRIVATE BANK & TRUST
            </h1>
            <p className="text-center text-gray-300 text-sm">INDEPENDENT TRUST FILING 2026</p>
            <p className="text-center text-gray-300 text-sm">CLIENT CLAIM SERVICE CONTRACT</p>
          </section>

          {/* Simple English Agreement */}
          <section>
            <h2 className="text-lg font-bold border-b-2 border-black pb-2 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              SIMPLE ENGLISH AGREEMENT
            </h2>
            
            <div className="space-y-4 text-sm">
              <div>
                <p className="font-bold">Parties</p>
                <p><strong>Service Provider:</strong> Quantum Law Consulting LLC - Ernest-Todd: Proffit, acting as Claimant and Trustee under EPIT Private Bank & Trust.</p>
                <p><strong>Client:</strong> {formData.name || '_______________'}</p>
              </div>

              <p>The Client authorizes the Service Provider to prepare, file, and administer lawful claims on their behalf related to:</p>
              <ul className="list-disc list-inside ml-4">
                <li>Foreclosure actions</li>
                <li>False arrest or incarceration</li>
                <li>Contract breaches</li>
                <li>Theft of services or materials</li>
                <li>Property title or possession disputes</li>
              </ul>
            </div>
          </section>

          <Separator />

          {/* Contract Terms */}
          <section>
            <h3 className="font-bold mb-2">1. Deposit and Proposal</h3>
            <p className="text-sm mb-4">
              The Client deposits $2,500.00 USD for initial review. Once the seven questions and Affidavit with the origin case/claim documents are received, the Provider will deliver a written proposal for the Claim within 2 business days. If the Provider cannot assist, the deposit is refunded same day. If accepted, the Claim will proceed to filing within 10 business days or as the client dictates thereafter. $3,000.00 for bonding payment upon finalizing claim filing. Either $2,500.00+500 for CUSIP or 1/12 of one percent for the additional surety. This will be outlined in the proposal. Note: claims can be processed without bonding, though not recommended.
            </p>

            <h3 className="font-bold mb-2">2. Bonded Performance</h3>
            <p className="text-sm mb-4">
              Each accepted Claim includes a Performance Bond value: $1,000,000 USD, guaranteed by EPIT Private Bank & Trust. If the Provider fails to perform, the bond covers liability. If successful, the Provider becomes a Co-Claimant, entitled to perform enforcement with the Client.
            </p>

            <h3 className="font-bold mb-2">3. Filing Timeline</h3>
            <ul className="text-sm list-disc list-inside mb-4">
              <li>Notice to Stop and Correct / Complaint Notice (21 days)</li>
              <li>Quo Warranto Complaint / Civil Filing (45-day Trust period + 3-day Right of Rescission) - stacked for DOJ/criminal</li>
              <li>Closure and Ledgering: Claim is registered after 69 total days if uncontested. Additional 10 days and 11 days for notice to perfect registered Bonds.</li>
              <li>The Claim is ledgered under QRIS (Quantum Registration Information System)</li>
              <li>Filed with EPIT Private Bank & Trust for underwriting and verification</li>
              <li>1/12 of 1% monthly. With 10% of the Bond-Surety claimed. Excelsior.</li>
            </ul>

            <h3 className="font-bold mb-2">4. Settlement</h3>
            <p className="text-sm mb-2">Upon closure:</p>
            <ul className="text-sm list-disc list-inside mb-4">
              <li>Client receives any cash settlement or property recovery + Judgement Bond</li>
              <li>Provider receives the corresponding Performance Bond</li>
              <li>All settlements are recorded and filed with: SEC, IRS, Treasury, and State Departments of Revenue, Department of Justice, financial crimes</li>
            </ul>

            <h3 className="font-bold mb-2">5. Identification and Authorization</h3>
            <p className="text-sm mb-2">Each party shall provide:</p>
            <ul className="text-sm list-disc list-inside mb-4">
              <li>Legal name, address, phone, and email</li>
              <li>Government-issued ID reference</li>
              <li>Fingerprint and autograph for recording</li>
            </ul>
            <p className="text-sm mb-4">Both parties agree by volition to all terms herein by autograph.</p>

            <h3 className="font-bold mb-2">6. Legal Notice</h3>
            <p className="text-sm mb-4">
              This agreement is for administrative and claim purposes only. The Provider is not acting as a licensed attorney, but as a Bonded Claimant and Fiduciary. All filings are administrative civil remedies under commercial and trust law. All claims will be filed with the Department of Justice and the Attorney General of the state for the criminal acts of the parties concerned. The Claimant will be authorized as a Whistleblower regarding the criminal claims. As a Whistleblower we have you positioned for % of the punitive damages. This is a separate case from our civil action, in which we are a claimant.
            </p>
          </section>

          <Separator />

          {/* Quantum Contract - Tandem Version */}
          <section className="bg-gray-100 p-4 text-xs font-mono">
            <p className="font-bold mb-2">QUANTUM CONTRACT - TANDEM VERSION</p>
            <p>
              FOR THIS-DOCUMENT-CONTRACT OF THE :EPIT-PRIVATE-BANK-AND-TRUST IS WITH THE KNOWLEDGE OF THE :ERNEST-TODD: PROFFIT-AS-THE-CLAIMANT-TRUSTEE FOR THE CLIENT'S-CLAIM-AUTHORIZATION-CONTRACT.
            </p>
            <p className="mt-2">
              FOR THE CONSIDERATION OF THE $2,500.00-USD IS WITH THE COMMENCEMENT OF THE CLAIM'S-REVIEW-AND-PROPOSAL-WITH A TWO-DAY-TIME-LIMIT-AND A TEN-DAY-PERFORMANCE-PERIOD FOR THE CLAIM-PACKAGE.
            </p>
            <p className="mt-2">
              FOR THE CLAIM'S-FILING IS WITH THE NOTICE-TIME OF TWENTY-ONE-DAYS PLUS FORTY-FIVE-DAYS AND THREE-DAYS OF RIGHT-OF-RECESSION = SIXTY-NINE-DAYS FOR THE TRUST-LAW-CLOSURE.
            </p>
            <p className="mt-2">
              FOR THE PERFORMANCE-FAILURE IS WITH THE LIABILITY-BOND OF ONE-MILLION-DOLLARS, BACKED AND LEDGERED BY THE :EPIT-PRIVATE-BANK-AND-TRUST.
            </p>
            <p className="mt-2">
              FOR THE PERFORMANCE-SUCCESS IS WITH THE CO-CLAIMANT-AUTHORITY FOR THE CLIENT AND CLAIMANT IN JOINDER WITH THE FEDERAL-FILING-AGENCIES.
            </p>
            <p className="mt-2">
              FOR THE IDENTIFICATION OF EACH PARTY IS WITH THE NAME, ADDRESS, EMAIL, ID, AUTOGRAPH, AND THUMBPRINT FOR THE LAWFUL-AUTHORITY OF THIS-DOCUMENT-CONTRACT-CLAIM.
            </p>
            <p className="mt-2">
              FOR THE PURPOSE OF THIS-CONTRACT IS WITH THE PERFORMANCE-OF-THE-CLAIM-SERVICES AND THE PRIVATE-FEDERAL-FILING UNDER THE :EPIT-PRIVATE-BANK-AND-TRUST-LAWFUL-JURISDICTION.
            </p>
          </section>

          <Separator />

          {/* Summary Table */}
          <section>
            <h2 className="text-lg font-bold border-b-2 border-black pb-2 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              SUMMARY OF PERFORMANCE AND BONDING ALLOCATIONS
            </h2>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Description</th>
                  <th className="border p-2 text-left">Amount (USD)</th>
                  <th className="border p-2 text-left">Ledger</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border p-2">Deposit Consideration</td>
                  <td className="border p-2">$2,500.00</td>
                  <td className="border p-2">EPIT Intake Ledger</td>
                </tr>
                <tr>
                  <td className="border p-2">Minimum Performance Bond</td>
                  <td className="border p-2">$2,500.00+$500-CUSIP or 1/12-1% for $1,000,000.00</td>
                  <td className="border p-2">EPIT Trust Bond</td>
                </tr>
                <tr>
                  <td className="border p-2">Refund Guarantee (if declined)</td>
                  <td className="border p-2">$2,500.00</td>
                  <td className="border p-2">Immediate Return</td>
                </tr>
                <tr>
                  <td className="border p-2">Trust Filing Duration</td>
                  <td className="border p-2">69 Days</td>
                  <td className="border p-2">QRIS Record</td>
                </tr>
                <tr>
                  <td className="border p-2">Federal Agencies Filed</td>
                  <td className="border p-2" colSpan="2">SEC / IRS / Treasury / State Rev / DOJ / FCU</td>
                </tr>
              </tbody>
            </table>
          </section>

          <Separator />

          {/* Authorization Section */}
          <section>
            <h2 className="text-lg font-bold border-b-2 border-black pb-2 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              AUTOGRAPHS / AUTHORIZATION
            </h2>
            
            <div className="bg-yellow-50 border border-yellow-200 p-4 mb-4 text-sm">
              <p className="font-medium">By authorizing below, I acknowledge that:</p>
              <ul className="mt-2 space-y-1 text-gray-700">
                <li>• I have read and understand all terms and conditions</li>
                <li>• The information provided is accurate to the best of my knowledge</li>
                <li>• I authorize Quantum LAW Consulting to act on my behalf as specified</li>
                <li>• I agree to the deposit fee of $2,500.00 USD</li>
              </ul>
            </div>

            {!readOnly && (
              <div className="flex items-start gap-3 mb-4">
                <input 
                  type="checkbox" 
                  id="agree-terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1"
                  data-testid="agree-terms-checkbox"
                />
                <label htmlFor="agree-terms" className="text-sm cursor-pointer">
                  I have read, understand, and agree to all terms and conditions outlined in this document. 
                  I acknowledge that my electronic authorization below is legally binding.
                </label>
              </div>
            )}

            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-2">Client Authorization:</p>
                {readOnly && signatureData ? (
                  <div className="border-2 border-gray-300 bg-white p-2">
                    <img src={signatureData} alt="Client Authorization" className="max-h-24 mx-auto" />
                  </div>
                ) : !readOnly ? (
                  <>
                    <div className="border-2 border-gray-300 bg-white">
                      <canvas
                        ref={canvasRef}
                        className="w-full h-32 cursor-crosshair touch-none"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        data-testid="authorization-canvas"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      Authorize above with mouse or finger
                    </p>
                  </>
                ) : (
                  <p className="border-b-2 border-black py-3">_______________</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-2">Date:</p>
                <p className="font-medium py-3 border-b-2 border-black">{currentDate}</p>
                
                <p className="text-sm text-gray-500 mb-2 mt-4">Printed Name:</p>
                <p className="font-medium py-3 border-b-2 border-black">{formData.name || '_______________'}</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
              <div>
                <p className="text-gray-500 mb-1">Ernest-Todd: Proffit (Trustee, Claimant, Witness, Litigator)</p>
                <p className="border-b border-gray-400 py-2">_______________</p>
                <p className="text-xs text-gray-500 mt-1">Date: _______________</p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">Witness / Notary:</p>
                <p className="border-b border-gray-400 py-2">_______________</p>
                <p className="text-xs text-gray-500 mt-1">Date: _______________</p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="bg-gray-100 p-4 text-center text-xs text-gray-500">
          <p>(Prepared and formatted by EPIT Private Bank & Trust — Independent Trust Filing 2026)</p>
          <p className="mt-1">Document generated on {currentDate}</p>
        </div>
      </div>

      {/* Action Buttons */}
      {!readOnly && onSign && (
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={clearAuthorization}
            disabled={!hasAuthorization || disabled}
            className="flex-1 rounded-none"
            data-testid="clear-authorization-btn"
          >
            <Eraser className="w-4 h-4 mr-2" />
            Clear Authorization
          </Button>
          <Button
            onClick={handleAuthorize}
            disabled={!hasAuthorization || !agreedToTerms || disabled}
            className="flex-1 rounded-none bg-green-600 text-white hover:bg-green-700"
            data-testid="confirm-authorize-btn"
          >
            <Check className="w-4 h-4 mr-2" />
            Authorize & Continue to Payment
          </Button>
        </div>
      )}
    </div>
  );
};

export default SignableDocument;
